import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playPopSound, playWinFanfare, playTickSound } from '../utils/soundEffects';
import { getSettings } from '../utils/db';

const { FiGift, FiAward, FiStar, FiZap, FiKey } = FiIcons;

const CHESTS = [
  { 
    id: 1, 
    name: 'Golden Sun Chest', 
    emoji: '👑', 
    gradient: 'from-amber-400 via-yellow-500 to-amber-600', 
    accent: 'border-amber-300 text-amber-500', 
    sparkle: '✨' 
  },
  { 
    id: 2, 
    name: 'Sapphire Galaxy Vault', 
    emoji: '💎', 
    gradient: 'from-blue-500 via-indigo-600 to-purple-600', 
    accent: 'border-blue-300 text-blue-500', 
    sparkle: '⭐' 
  },
  { 
    id: 3, 
    name: 'Emerald Dragon Coffer', 
    emoji: '🐉', 
    gradient: 'from-emerald-400 via-teal-500 to-emerald-700', 
    accent: 'border-emerald-300 text-emerald-500', 
    sparkle: '🌟' 
  },
  { 
    id: 4, 
    name: 'Ruby Royal Lockbox', 
    emoji: '🔮', 
    gradient: 'from-rose-500 via-pink-600 to-red-700', 
    accent: 'border-rose-300 text-rose-500', 
    sparkle: '⚡' 
  }
];

const TreasureBoxGame = ({ prizes = [], onFinished, isDarkMode = false }) => {
  const [openedId, setOpenedId] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [wonPrize, setWonPrize] = useState('');

  const safePrizes = prizes && prizes.length > 0 ? prizes : [
    "🧢 School Champion Cap", 
    "🎖️ History Maestro Badge", 
    "🔬 Science Whiz Badge", 
    "⭐ Star Student Medal",
    "🎁 Deluxe Stationery Kit", 
    "🎨 Creative Art & Sketch Set"
  ];

  const handleOpenChest = (chest) => {
    if (openedId !== null) return;
    setOpenedId(chest.id);

    const settings = getSettings();
    if (settings.soundEnabled !== false) {
      playTickSound(520, 0.15);
      setTimeout(() => playPopSound(), 300);
    }

    const randomPrize = safePrizes[Math.floor(Math.random() * safePrizes.length)];
    setWonPrize(randomPrize);

    setTimeout(() => {
      setIsRevealed(true);
      if (settings.soundEnabled !== false) {
        playWinFanfare();
      }
      setTimeout(() => {
        if (onFinished) onFinished(randomPrize);
      }, 1900);
    }, 600);
  };

  return (
    <div className="relative w-full max-w-xl mx-auto py-6 select-none flex flex-col items-center">
      
      {/* Title instructions */}
      <div className="text-center mb-6">
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 mb-2 shadow-xs ${
          isDarkMode 
            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
            : 'bg-amber-100 text-amber-800 border border-amber-300'
        }`}>
          <SafeIcon icon={FiKey} /> Kids Stall Mystery Kiosk
        </span>
        <h3 className={`text-2xl md:text-3xl font-extrabold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Tap Any Magic Chest to Unlock!
        </h3>
        <p className={`text-xs md:text-sm mt-1 font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
          Pick your secret treasure box to unlock caps, badges & gifts.
        </p>
      </div>

      {/* 4 Interactive 3D Mystery Chests */}
      <div className="grid grid-cols-2 gap-4 sm:gap-6 w-full px-2 justify-items-center">
        {CHESTS.map((chest, idx) => {
          const isThisOpened = openedId === chest.id;
          const isOtherOpened = openedId !== null && !isThisOpened;

          return (
            <motion.div
              key={chest.id}
              onClick={() => handleOpenChest(chest)}
              whileHover={!openedId ? { scale: 1.05, y: -4 } : {}}
              whileTap={!openedId ? { scale: 0.95 } : {}}
              className={`w-full p-5 sm:p-6 rounded-3xl border-2 text-center relative overflow-hidden transition-all cursor-pointer shadow-lg ${
                isThisOpened
                  ? 'border-amber-400 bg-amber-500/10 shadow-[0_0_30px_rgba(245,158,11,0.4)] animate-pulse'
                  : isOtherOpened
                    ? 'opacity-30 border-slate-300 dark:border-slate-800'
                    : (isDarkMode ? 'bg-slate-900/90 border-slate-700 hover:border-amber-400' : 'bg-white border-slate-200 hover:border-amber-400 hover:shadow-xl')
              }`}
            >
              {/* Chest Icon & Lid */}
              <div className="relative mb-3 flex items-center justify-center">
                <motion.div
                  animate={isThisOpened ? { rotate: [0, -10, 10, -5, 5, 0], scale: [1, 1.2, 1.1] } : {}}
                  transition={{ duration: 0.5 }}
                  className={`w-18 h-18 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-tr ${chest.gradient} text-white text-3xl sm:text-4xl flex items-center justify-center shadow-lg mx-auto border-2 border-white/20`}
                >
                  {isThisOpened ? '✨' : chest.emoji}
                </motion.div>

                {/* Keyhole Badge */}
                <div className="absolute -bottom-1 bg-slate-950 text-amber-400 text-xs px-2 py-0.5 rounded-full font-bold border border-amber-400/40 shadow-sm flex items-center gap-1">
                  <SafeIcon icon={isThisOpened ? FiZap : FiKey} />
                  <span>{isThisOpened ? 'OPEN' : 'LOCKED'}</span>
                </div>
              </div>

              <h4 className={`font-black text-sm sm:text-base mt-2 ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}>
                {chest.name}
              </h4>
              <span className={`text-[10px] font-bold uppercase tracking-wider block mt-0.5 ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}>
                Box #{idx + 1}
              </span>
            </motion.div>
          );
        })}
      </div>

      {/* Prize Reveal Modal */}
      <AnimatePresence>
        {isRevealed && (
          <motion.div
            initial={{ scale: 0.7, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            className={`mt-6 p-6 rounded-3xl border-2 text-center shadow-2xl backdrop-blur-md max-w-sm w-full mx-auto ${
              isDarkMode 
                ? 'bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 border-amber-400 text-white' 
                : 'bg-white border-amber-400 text-slate-900 shadow-xl'
            }`}
          >
            <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-amber-400 to-yellow-300 text-slate-950 font-black text-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
              🎁
            </div>
            <span className="text-xs uppercase font-extrabold text-amber-500 tracking-wider">
              Secret Treasure Unlocked!
            </span>
            <h4 className={`text-xl md:text-2xl font-black mt-1 mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {wonPrize}
            </h4>
            <p className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Preparing your official digital prize pass...
            </p>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default TreasureBoxGame;
